import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn } from "lucide-react";
import api from "@/lib/api";
import { useAuth, apiErr } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [forgot, setForgot] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  useEffect(() => { if (user) nav(user.role === "PENCATAT" ? "/operasional-jahitan" : "/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { const u = await login(email.trim(), password); toast.success("Selamat datang"); nav(u.role === "PENCATAT" ? "/operasional-jahitan" : "/", { replace: true }); }
    catch (ex) { setErr(apiErr(ex)); } finally { setLoading(false); }
  };

  const sendForgot = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { const { data } = await api.post("/auth/forgot-password", { email: email.trim() }); setForgotMsg(data.message); }
    catch (ex) { setErr(apiErr(ex)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#1D1D1F] text-white p-12">
        <div className="flex items-center gap-4">
          <img src="/logo-meranggi.png" alt="Logo Meranggi Garment" className="w-16 h-16 rounded-full" data-testid="login-logo" />
          <div>
            <div className="text-xs font-semibold tracking-widest text-[#86868B] uppercase">CV. Meranggi Jaya</div>
            <div className="text-3xl font-semibold tracking-tight mt-1">MERANGGI LEDGER</div>
            <div className="text-sm text-[#86868B] tracking-wide uppercase">Since 1981 · Production Control · General Ledger</div>
          </div>
        </div>
        <div className="space-y-4 max-w-md">
          <p className="text-[#D2D2D7] text-lg leading-relaxed no-up">Sistem kontrol produksi garment: jahitan keluar, setoran, reject, open WIP, bon barang pembantu, ongkos jahit, dan pembayaran — satu sumber data resmi.</p>
          <div className="grid grid-cols-3 gap-3 text-center">{["Data Akurat", "Audit Trail", "Query Cepat"].map((t) => <div key={t} className="bg-white/10 rounded-xl py-3 text-sm font-medium">{t}</div>)}</div>
        </div>
        <div className="text-xs text-[#86868B]">WITA · Asia/Makassar</div>
      </div>

      <div className="flex items-center justify-center p-5 sm:p-8 bg-[#F5F5F7]">
        <form onSubmit={forgot ? sendForgot : submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden mb-4 flex items-center gap-3">
            <img src="/logo-meranggi.png" alt="Logo" className="w-12 h-12 rounded-full" />
            <div>
              <div className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">CV. Meranggi Jaya</div>
              <div className="text-2xl font-extrabold tracking-tight text-slate-900">MERANGGI LEDGER</div>
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{forgot ? "Lupa Kata Sandi" : "Masuk"}</h1>
            <p className="text-sm text-slate-500 mt-1 no-up">{forgot ? "Masukkan email akun Anda. Permintaan reset akan diberitahukan ke pemilik." : "Setiap akses wajib login dengan akun yang terdaftar."}</p>
          </div>
          <div>
            <Label>Email atau Username</Label>
            <Input type="text" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 text-base" data-testid="login-email-input" placeholder="email atau username" />
          </div>
          {!forgot && <div>
            <Label>Kata Sandi</Label>
            <div className="relative">
              <Input type={show ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 text-base pr-11" data-testid="login-password-input" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-500" aria-label="tampilkan sandi" data-testid="login-toggle-password">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            </div>
          </div>}
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 no-up" data-testid="login-error">{err}</div>}
          {forgotMsg && <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 no-up" data-testid="forgot-message">{forgotMsg}</div>}
          <Button type="submit" disabled={loading} className="w-full h-11 text-base bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full font-medium" data-testid="login-submit-button">
            <LogIn className="w-4 h-4 mr-2" />{loading ? "Memproses…" : forgot ? "Kirim Permintaan Reset" : "Masuk"}
          </Button>
          <button type="button" onClick={() => { setForgot((f) => !f); setErr(""); setForgotMsg(""); }} className="w-full text-sm text-slate-600 underline py-2" data-testid="login-forgot-toggle">
            {forgot ? "← Kembali ke halaman masuk" : "Lupa kata sandi?"}
          </button>
        </form>
      </div>
    </div>
  );
}
